import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSetAtom } from 'jotai';
import { useEffect, useRef } from 'react';

import { fetchData } from '@/api/client';
import { webSocketService } from '@/services/websocket';
import {
  currentBusinessAtom,
  currentUserAtom,
  isAuthenticatedAtom,
  roleAtom,
  User,
} from '@/store/atoms';
import { Business } from '@/types/businesses';

interface AuthResponse {
  success: boolean;
  data: {
    authenticated: boolean;
    message: string;
    user: User;
    business: Business;
    tokenExpiresIn: string;
  };
  timestamp: string;
  message: string;
}

const fetchAuthVerify = async (): Promise<AuthResponse> => {
  return fetchData<AuthResponse>({
    path: '/api/auth/verify',
  });
};

export const useAuthQuery = () => {
  const queryClient = useQueryClient();
  const setIsAuthenticated = useSetAtom(isAuthenticatedAtom);
  const setCurrentBusiness = useSetAtom(currentBusinessAtom);
  const setCurrentUser = useSetAtom(currentUserAtom);
  const setRole = useSetAtom(roleAtom);
  const wsInitialized = useRef(false);

  const query = useQuery({
    queryKey: ['auth', 'verify'],
    queryFn: fetchAuthVerify,
    staleTime: Infinity, // Never refetch unless explicitly invalidated
    gcTime: Infinity, // Keep in cache forever
    retry: false, // Don't retry on 401/403
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });

  // Sync query data with atoms and initialize WebSocket
  useEffect(() => {
    if (query.isSuccess) {
      const isAuthenticated = query.data.data.authenticated;
      setIsAuthenticated(isAuthenticated);
      setCurrentBusiness(query.data.data.business);
      setCurrentUser(query.data.data.user);
      setRole(query.data.data.user.role as 'superadmin' | 'admin' | 'user');

      // Initialize WebSocket ONLY after successful authentication
      if (isAuthenticated && !wsInitialized.current) {
        wsInitialized.current = true;
        webSocketService.init(queryClient);

        // Setup cleanup on window unload
        const handleBeforeUnload = () => {
          webSocketService.disconnect();
        };
        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
          window.removeEventListener('beforeunload', handleBeforeUnload);
        };
      }
    } else if (query.isError) {
      setIsAuthenticated(false);
      setCurrentBusiness(null);
      setCurrentUser(null);
      setRole(null);
      wsInitialized.current = false;
    }
  }, [
    query.isSuccess,
    query.isError,
    query.data,
    queryClient,
    setIsAuthenticated,
    setCurrentBusiness,
    setCurrentUser,
    setRole,
  ]);

  return {
    isAuthenticated: query.data?.data.authenticated ?? false,
    isLoading: query.isLoading,
    isError: query.isError,
    user: query.data?.data.user ?? null,
    business: query.data?.data.business ?? null,
    authChecked: !query.isLoading,
  };
};
